import React from "react";
import * as _ from "lodash";
import * as moment from "moment";

import * as SmartDrawings from "../../smartDrawings";
import * as ColorUtils from "../measurementData/colorUtils";
import { viewerOptions } from "../../viewer";
import { QuickFilterUtils } from "../filter/quickFilterUtils";

interface LegendProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    scheme: SmartDrawings.ColorScheme;
}

interface ColorProps {
    color: SmartDrawings.ColorData;
}

interface LegendState {
    colorProperty: SmartDrawings.PropertyDefinition;
}

interface GradientProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    range: SmartDrawings.RangeColorScheme;
}

interface GradientUiProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
}

interface GradientUiState {
    colorSchemes: SmartDrawings.ColorScheme[];
    ranges: SmartDrawings.RangeColorScheme[];
    colorProps: SmartDrawings.PropertyDefinition[];
    colorProperty: SmartDrawings.PropertyDefinition;
    paramsToDbIdsMapped: boolean;
}

const backgroundColor = ({ color }: SmartDrawings.ColorData) => ({
    backgroundColor: `rgba(${color.r},${color.g},${color.b},${color.a})`
});

const ColorRow: React.SFC<ColorProps> = (props) => {
    return <li className="color-item"><div className="color-icon" style={backgroundColor(props.color)} /><div className="color-label">{
        props.color.description}</div></li>;
}

export class ColorListLegend extends React.Component<LegendProps, LegendState> {

    constructor(props) {
        super(props);
    }

    render() {
        return (
            <div className="ml-2">
                <ul className="plain-list">
                {Object["values"](this.props.scheme.colors).map(color => <ColorRow key={color.id} color={color} />)}
                </ul>
            </div>
        );
    }
}

const SelectOption = props => (<option value={props.name}>{props.description}</option>);

const SelectField = (props) => {
    return <div className="form-group row">
        <label className="col-form-label col-4 text-truncate">{props.label}</label>
        <div className="col-6">
            <select className="form-control" onChange={(e) => {
                props.callback(e.target.value);

            }}>
                {props.values.map(v => (<SelectOption key={v.name} {...v} />))}
            </select>
        </div>
    </div>;
}

class ColorGradient extends React.Component<GradientProps> {

    private points: SmartDrawings.RangePoint[];

    constructor(props) {
        super(props);
        this.points = this.props.range.range; 
    }

    render() {
        return (
            <div className="ml-2">
                <ul className="color-legend-horizontal" style={{ width: "95%" }}>
                    {this.points.map((s, i) =>
                        (<li key={s.value}>
                             <div className="color-legend-item" style={getStyleColor(ColorUtils.getRgb(s.value, this.props.range))}></div>
                             {i > 0 && <div>{s.value}</div>}
                         </li>))}
                </ul>
            </div>
        );
    }
}

const getStyleColor = (rgb) => {
    return { backgroundColor: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},1)`, color: "black" };
};

const Pending = () => {
    return <div>
               <div className="row justify-content-center">
                   <i className="icon ion-md-hourglass wait-spinner" />
               </div>
           </div>;
}

export default class ColorLegend extends React.Component<GradientUiProps, GradientUiState> {

    private coi: boolean = false;

    constructor(props) {
        super(props);

        const colorProps = getColorProperties();
        this.state = {
            colorSchemes: getColorSchemes(),
            ranges: getColorRanges(),
            colorProps: colorProps,
            colorProperty: _.isEmpty(colorProps) ? null : colorProps[0],
            paramsToDbIdsMapped: false
        }
    }

    componentDidMount(): void {
        this.props.viewer.addEventListener(SmartDrawings.SdEvents.EXTERNAL_DATA_LOADED, this.onParamsMapped);
        this.props.viewer.addEventListener(SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED, this.onParamsMapped);
        this.props.viewer.addEventListener(SmartDrawings.SdEvents.EXTERNAL_DATA_CHANGED, this.onExternalDataChanged);
        this.setNodeColors(this.state.colorProperty);
    }

    componentWillUnmount(): void {
        this.props.viewer.removeEventListener(SmartDrawings.SdEvents.EXTERNAL_DATA_LOADED, this.onParamsMapped);
        this.props.viewer.removeEventListener(SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED, this.onParamsMapped);
        this.props.viewer.removeEventListener(SmartDrawings.SdEvents.EXTERNAL_DATA_CHANGED, this.onExternalDataChanged);
        this.clearAllColors(this.coi);
    }

    onParamsMapped = () => {
        const colorProps = getColorProperties();
        const colorProp = _.isEmpty(colorProps) ? null : colorProps[0];
        this.setState({
            colorSchemes: getColorSchemes(),
            ranges: getColorRanges(),
            colorProps: colorProps,
            colorProperty: _.isEmpty(colorProps) ? null : colorProps[0],
            paramsToDbIdsMapped: true
        });
        
        this.setNodeColors(colorProp);
    }

    onExternalDataChanged = () => {
        let colorProp = this.state.colorProperty;
        const colorProps = getColorProperties();
        if (!_.find(colorProps, p => p.name === colorProp.name))
            colorProp = _.isEmpty(colorProps) ? null : colorProps[0];
        this.setState({
            colorSchemes: getColorSchemes(),
            ranges: getColorRanges(),
            colorProps: colorProps,
            colorProperty: colorProp,
            paramsToDbIdsMapped: true
        });

        this.setNodeColors(colorProp);
    }

    onSelectColorProperty = async (propName) => {
        const colorProp = _.find(this.state.colorProps, cp => cp.name === propName);
        this.setState({ colorProperty: colorProp });
        this.setNodeColors(colorProp);
    }

    render() {
        
        const pending = (this.state.colorSchemes == null && this.state.ranges == null);
        return (<div>
            {pending
                ? <Pending />
                : <div className="react-content">
                <form className="container pt-3" style={{ width: "auto" }}>
                    <SelectField label={"Displayed data"} values={this.state.colorProps} callback={this.onSelectColorProperty}/>
                </form>
                {this.state.colorSchemes != null && <ColorListLegend scheme={this.getColorScheme(this.state.colorProperty)} viewer={this.props.viewer} />}
                {this.state.ranges != null && <ColorGradient range={this.getColorRange(this.state.colorProperty)} viewer={this.props.viewer} />}
            </div>
        }</div>);
    }

    private getSchemeColor(schemeId: string, colorId: string): THREE.Vector4 {
        const scheme = _.find(this.state.colorSchemes, s => s.id === schemeId);
        const colorVal = scheme.colors[colorId];
        if (colorVal.vector) {
            return colorVal.vector;
        }

        const vector = ColorUtils.makeColorVector(colorVal.color);
        colorVal.vector = vector;
        return vector;
    }

    private clearAllColors(keepIsolated: boolean = false) {
        const models = this.props.viewer.getVisibleModels();
        for (let model of models) {
            this.props.viewer.clearThemingColors(model);
        }

        if (!keepIsolated) {
            this.props.viewer.showAll();
        }
    }

    private async setNodeColors(colorProperty: SmartDrawings.PropertyDefinition) {
        const models = this.props.viewer.getVisibleModels();

        const data: SmartDrawings.ExternalData = viewerOptions.externalData;
        if (data == null || data.idProperty != null && data.paramsToDbIds == null) {
            return;
        }

        const range = this.getColorRange(colorProperty);
        const scheme = this.getColorScheme(colorProperty);

        // Check url parameter
        this.coi = !_.isNil(new URLSearchParams(window.location.search).get(SmartDrawings.SdUrlParameters.COLOR_ONLY_ISOLATED));

        this.clearAllColors(this.coi);

        const isolated = [];

        // We may be coloring elements or nodes.
        const { isNode } = colorProperty;
        const hiddenModel = isNode ? models[0] : models[1];
        const displayedModel = isNode ? models[1] : models[0];

        const guids = isNode ? _.keys(data.nodes.propertyValues) : _.keys(data.propertyValues);
        for (let guid of guids) {
            const values = isNode ? data.nodes.propertyValues[guid] : data.propertyValues[guid];
            if (!_.isEmpty(values[colorProperty.name])) {
                // The dbId can be found in different places according to the data.
                let dbId = null;
                const { idProperty } = data;
                if (idProperty != null) {
                    // The data tells which property is used to map to the dbId.
                    dbId = viewerOptions.externalData.paramsToDbIds[guid];
                } else {
                    if (isNode) {
                        // In case of connecting nodes, the dbId already is the guid given in the data.
                        dbId = Number(guid);
                    } else {
                        // Normal guid to dbId mapping.
                        dbId = viewerOptions.idMap.nodeId(null, guid);
                    }
                }

                if (dbId != null) {
                    if (this.coi ? this.props.viewer.isNodeVisible(dbId, null) : true) {
                        this.props.viewer.setThemingColor(dbId,
                            range != null
                            ? ColorUtils.getColorVector(this.getNumber(values[colorProperty.name]), range)
                            : this.getSchemeColor(scheme.id, values[colorProperty.name]),
                            displayedModel,
                            true);
                        isolated.push(dbId);
                    }
                }
            }
        }

        if (hiddenModel != null) {
            // Basically hide everything, so give a made-up dbId to satisfy the viewer.
            this.props.viewer.isolate([1000000], hiddenModel);
        }

        if (!this.coi) {
            this.props.viewer.isolate(isolated, displayedModel);
        }
    }

    private getNumber(value: string): number {
        return Number(value.replace(",", "."));
    }

    private getColorRange(colorProperty: SmartDrawings.PropertyDefinition) {
        if (this.state.ranges != null) {
            return _.find(this.state.ranges, r => r.id === colorProperty.legendId);
        }
    }

    private getColorScheme(colorProperty: SmartDrawings.PropertyDefinition) {
        if (this.state.colorSchemes != null) {
            return _.find(this.state.colorSchemes, r => r.id === colorProperty.legendId);
        }
    }
}

const getColorProperties = (): SmartDrawings.PropertyDefinition[] => {
    const extData: SmartDrawings.ExternalData = viewerOptions.externalData;
    if (extData == null || (extData.rangeSchemes == null && extData.idSchemes == null)) {
        return null;
    } else {
        const elementColorProperties = filterColorProperties(extData.propertyDefinitions);
        const nodeColorProperties = filterColorProperties(extData.nodes?.propertyDefinitions).map(cp => {
            cp.isNode = true;
            return cp;
        });
        return [...elementColorProperties, ...nodeColorProperties];
    }
}

const filterColorProperties = (propertyDefinitions) => {
    return _.filter(_.values(propertyDefinitions), pd => !_.isEmpty(pd.legendId));
}

const getColorRanges = (): SmartDrawings.RangeColorScheme[] => {
    const extData: SmartDrawings.ExternalData = viewerOptions.externalData;
    if (extData == null || extData.rangeSchemes == null) {
        return null;
    } else {
        return extData.rangeSchemes.colorRanges;
    }
}

const getColorSchemes = (): SmartDrawings.ColorScheme[] => {
    const extData: SmartDrawings.ExternalData = viewerOptions.externalData;
    if (extData == null || extData.idSchemes == null) {
        return null;
    } else {
        return extData.idSchemes.customColorSchemes;
    }
}